import { formatDate, isISODateString, isNumberStr } from '@topcon/topcon-utils';

import { Filter, FilterIDs, FilterNumberValue, FilterValue } from './filter.types';
import { distanceUnits, timeUnits } from './filter.constants';
import {
  Operator,
  OperatorSymbol,
  defaultOperatorArr,
  operatorSymbolsMap,
} from './filterOperator.types';

const dateFields: string[] = [];

/**
 * Get the symbol for the operator e.g. 'gte' -> '>='
 * @param {string} s The operator name
 * @returns {string} The symbol
 */
export function toOperatorSymbol(s: Operator): OperatorSymbol {
  return operatorSymbolsMap[s] || OperatorSymbol.eq;
}

/**
 * Check if the value is string or number
 * @param {unknown} v The value to check
 * @returns {boolean} Whether the value is string or number
 */
function isStrOrNumber(v: unknown): v is string | number {
  return ['number', 'string'].includes(typeof v);
}

/**
 * Turn the value into array of string or number
 * @param {FilterValue} v The value
 * @returns {(number | string)[]} The result array
 */
function toNumOrStrArr(v: FilterValue): (number | string)[] {
  return Array.isArray(v) ? v : isStrOrNumber(v) ? [v] : [];
}

/**
 * Check if filter value is empty
 * @param {FilterValue} v The filter value
 * @returns {boolean} Whether it is empty
 */
export function isEmptyFilterValue(v: FilterValue): boolean {
  return (typeof v !== 'number' && !v) || (Array.isArray(v) && v.length === 0);
}

/**
 * Compare two filters by values
 * @param {Filter} a The first filter
 * @param {Filter} b The second filter
 * @returns {boolean} Whether the two filters are equal
 */
export function compareFilters(a: Filter, b: Filter): boolean {
  const [av, bv] = [a.value, b.value];
  if (isFilterNumberValue(av) !== isFilterNumberValue(bv)) return false;
  if (isFilterNumberValue(av) && isFilterNumberValue(bv)) {
    const akv = Object.entries(av);
    const bkv = Object.entries(bv);
    return (
      akv.length === bkv.length && akv.every(([k, v]) => bv[k as keyof FilterNumberValue] === v)
    );
  }
  const aValue = toNumOrStrArr(av);
  const bValue = toNumOrStrArr(bv);
  if (aValue.length !== bValue.length) return false;
  const aSorted = [...aValue].sort();
  const bSorted = [...bValue].sort();
  return aSorted.every((v, i) => v === bSorted[i]);
}

/**
 * Compare two filter arrays for structural equality by name, active state, and values.
 */
export function areFilterArraysEqual(a: Filter[], b: Filter[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((f) => {
    const matching = b.find((g) => g.name === f.name);
    return matching !== undefined && matching.active === f.active && compareFilters(f, matching);
  });
}

/**
 * Convert a value to number, if fail returns a default number
 * @param {unknown} v The value to convert
 * @param {number} d The default number
 * @returns {number} The converted number if applicable
 */
function toNumber(v: unknown, d: number): number {
  const numOrNan = Number(v);
  return isNaN(numOrNan) ? d : numOrNan;
}

/**
 * Check if filter has an error e.g. number value negative
 * @param {Filter} filter The filter to check
 * @returns {boolean} Whether the filter can be applied
 */
export function hasError(filter: Filter): boolean {
  const { value } = filter;
  return isFilterNumberValue(value) && !!value.operand && toNumber(value.operand, -1) < 0;
}

/**
 * Check if filter can be applied.
 * Numeric filters with missing fields cannot be applied
 * @param {Filter} filter The filter to check
 * @returns {boolean} Whether the filter can be applied
 */
export function isFilterApplicable(filter: Filter): boolean {
  const { value } = filter;
  if (!isFilterNumberValue(value)) return true;
  return !!value.operator && !!value.operand && toNumber(value.operand, -1) >= 0;
}

/**
 * Check if a value is FilterNumberValue
 * @param {FilterValue} v The value to check
 * @returns {boolean} Whether the value is FilterValue
 */
export function isFilterNumberValue(v: FilterValue): v is FilterNumberValue {
  return (
    !!v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    ['operator', 'operand', 'unit'].some((k) => typeof v[k as keyof FilterNumberValue] === 'string')
  );
}

/**
 * Get time in milliseconds
 * @param {number} n The numeric value
 * @param {string} unit The time unit
 * @returns {number} The time in ms
 */
export function toMilliseconds(n: number, unit: string): number {
  const multiplier = { ms: 1, s: 1000 }[unit] || 1;
  return n * multiplier;
}

/**
 * Get distance in meters
 * @param {number} n The numeric value
 * @param {string} unit The time unit
 * @returns {number} The distance in meters
 */
export function toMeters(n: number, unit: string): number {
  const multiplier = { m: 1, cm: 0.01, mm: 0.001 }[unit] || 1;
  return n * multiplier;
}

/**
 * Get the value in base unit e.g. 10s -> 10000ms
 * @param {string} numStr The numeric string
 * @param {string} unit The time unit
 * @returns {number} The converted value
 */
export function getNumericValue(numStr: string, unit: string): number {
  const numOrNan = Number(numStr);
  const num = isNaN(numOrNan) ? 0 : numOrNan;
  if (timeUnits.includes(unit)) return toMilliseconds(num, unit);
  if (distanceUnits.includes(unit)) return toMeters(num, unit);
  return num;
}

/**
 * Get a pair of [name, value] for query param from the filter
 * @param {Filter} filter The filter
 * @returns {[string, string]} Formatted name and value
 */
export function getNameValueForUrl(filter: Filter): [string, string] {
  const { name, value } = filter;
  if (!isFilterNumberValue(value)) {
    return [name, String(value)];
  }
  const { unit = '', operator = '', operand = '' } = value;
  const key = `${name}[${operator}]`;
  const numValue = getNumericValue(String(operand), unit);
  return [key, String(numValue)];
}

/**
 * Get subtext for a filter if applicable
 * @param {Filter} f The filter
 * @param {FilterValue} optionText The option text
 * @returns {string | undefined} The subtext value
 */
export function getSubtextForFilter(f: Filter, optionText: FilterValue): string | undefined {
  const { value, type } = f;
  if (isFilterNumberValue(value)) {
    const { unit = '', operator = Operator.eq, operand = '' } = value;
    return `${toOperatorSymbol(operator as Operator)} ${operand} ${unit}`.trim();
  }
  if (type === 'date' && typeof value === 'string') return formatDate(new Date(value));
  const isPlural = Array.isArray(value) && value.length > 1;
  return isPlural ? undefined : String(optionText);
}

/**
 * Get default unit for a numeric filter given the field name
 * @param {string} field The filter field name
 * @returns {string} The default unit
 */
export function getDefaultUnit(field: string): string {
  if (field === 'latency') return 'ms';
  // TODO: Add for other filters if applicable
  if (['x', 'y', 'z', 'posdiff'].includes(field)) return 'm';
  return '';
}

/**
 * Get default value for numeric filter
 * @param {string} field The filter field name
 * @returns {FilterNumberValue} The default value for the filter
 */
export function getDefaultNumericFilterValue(field: string): FilterNumberValue {
  return { operator: Operator.gt, operand: '', unit: getDefaultUnit(field) };
}

/**
 * Merge two filters by value. b overwrites a. Create a new filter
 * Only merge same type filters e.g. both are date filters
 * @param {Filter} a The first filter
 * @param {Filter} b The second filter
 * @returns {Filter} The merged filter
 */
export function mergeFilters(a: Filter, b: Filter): Filter {
  const { value: aValue } = a;
  const { value: bValue } = b;
  if (isFilterNumberValue(aValue) && isFilterNumberValue(bValue)) {
    return new Filter(b.target, b.name, b.text, { ...aValue, ...bValue }, b.active, b.type);
  }
  return b.copy();
}

/**
 * Get a list of [key, value] pairs from filters, for url params
 * @param {Filter[]} filters The filter array
 * @returns {[string, string][]} The list of [key, value] pairs
 */
export function getFilterParams(filters: Filter[]): [string, string][] {
  return filters.map(getNameValueForUrl);
}

function getNumberFieldKeys(k: string) {
  return Object.values(Operator).map((op) => `${k}[${op}]`);
}

/**
 * Restore number filter value from key value pair
 * @param {string} key The key
 * @param {string} value The value
 * @returns {FilterNumberValue} The restored value
 */
export function getNumberFilterValue(key: string, value: string): FilterNumberValue {
  const operator = defaultOperatorArr.find((v) => key.includes(`[${v}]`)) || Operator.eq;
  const [origKey] = key.split('[');
  return { operator, operand: value, unit: getDefaultUnit(origKey) };
}

/**
 * Generate an array of Filter from a URLSearchParams object
 * @param {FilterIDs} id ID to indicate the set of filters
 * @param {URLSearchParams} params The URLSearchParams object
 * @returns {Filter[]} The list of Filter
 */
export function getFiltersFromSearchParams(id: FilterIDs, params: URLSearchParams): Filter[] {
  // TODO: Add other prefixes
  const namePrefix = '';
  const addedFields = new Set();
  const filters = getSearchParamsEntries(params).reduce((acc, [k, v]) => {
    const text = `${namePrefix}${k}`;
    if (!k.includes('[')) {
      if (addedFields.has(k)) return acc;
      addedFields.add(k);
      const fValue = dateFields.includes(k) || !v.includes(',') ? v : v.split(',');
      acc.push(
        new Filter(id, k, text, fValue, true, dateFields.includes(k) ? 'date' : 'multiselect')
      );
      return acc;
    }
    const [origKey] = k.split('[');
    if (addedFields.has(origKey)) return acc;
    addedFields.add(origKey);
    const numText = `${namePrefix}${origKey}`;
    const fValue = getNumberFilterValue(k, v);
    acc.push(new Filter(id, origKey, numText, fValue, true, 'number'));
    return acc;
  }, [] as Filter[]);
  return filters;
}

/**
 * Check if a query string can be parsed to status
 * The string has to be e.g. '0', '1','0,1'
 * @param {string} v The string value
 * @returns {boolean} Whether the string can be parsed to status
 */
export function checkStatusValue(v: string): boolean {
  const statuses = ['0', '1'];
  const items = v.split(',');
  if (!items.length) return false;
  const itemSet = new Set(items);
  return itemSet.size === items.length && items.every((item) => statuses.includes(item));
}

/**
 * Check the key and value whether they are a valid filter entry
 * @param {string} key The key
 * @param {string} value The value
 * @returns {boolean} Whether the entry is valid
 */
export function validateKeyValue(key: string, value: string): boolean {
  const multiSelectFields: string[] = [];
  const numberFields: string[] = [];
  const numberFieldKeys = numberFields.reduce(
    (acc, k) => acc.concat(getNumberFieldKeys(k)),
    [] as string[]
  );
  if (key === 'status') return checkStatusValue(value);
  if (numberFieldKeys.includes(key)) return isNumberStr(value);
  if (dateFields.includes(key)) return isISODateString(value);
  if (multiSelectFields.includes(key)) return !!value;
  return false;
}

/**
 * Get the valid filter entries from an URLSearchParams object
 * @param {URLSearchParams} params The URLSearchParams object
 * @returns {[string, string][]} The valid filter entries
 */
export function getSearchParamsEntries(params: URLSearchParams): [string, string][] {
  const pairs = Array.from(params.entries());
  const numFields: string[] = [];
  const nonNumFields: string[] = [...dateFields];
  const numberFieldKeys = numFields.reduce(
    (acc, k) => acc.concat(getNumberFieldKeys(k)),
    [] as string[]
  );
  const addedNumberField = new Set();
  const validPairs = pairs.reduce(
    (acc, [k, v]) => {
      if (!validateKeyValue(k, v)) return acc;
      if (numberFieldKeys.includes(k)) {
        const [origK] = k.split('[');
        if (addedNumberField.has(origK)) return acc;
        addedNumberField.add(origK);
        acc.push([k, v]);
        return acc;
      }
      if (nonNumFields.includes(k)) {
        acc.push([k, v]);
      }
      return acc;
    },
    [] as [string, string][]
  );
  return validPairs;
}

/**
 * Get all entries for URL from a list of filters
 * @param {Filter[]} filters The filters
 * @returns {[string, string][]} The entries
 */
export function getEntriesFromFilters(filters: Filter[]) {
  return filters.map(getNameValueForUrl);
}

/**
 * Generate a URLSearchParams object from a list of filters
 * @param {Filter[]} filters The filters
 * @returns {URLSearchParams} The URLSearchParams object
 */
export function getSearchParamsFromFilters(filters: Filter[]): URLSearchParams {
  return new URLSearchParams(getEntriesFromFilters(filters));
}

/**
 * Compare two lists of entries
 * @param {[string, string][]} a The first list of entries
 * @param {[string, string][]} b The second list of entries
 * @returns {boolean} Whether they are equal
 */
export function compareEntries(a: [string, string][], b: [string, string][]): boolean {
  return a.length === b.length && a.every(([k, v], i) => k === b[i][0] && v === b[i][1]);
}
